import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../../context/AuthContext';
import { AdminLayout } from '../../../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { 
    Mail, 
    Send, 
    BarChart3, 
    MousePointerClick, 
    Eye, 
    ShoppingCart,
    TrendingUp,
    Plus,
    Calendar,
    Clock,
    CheckCircle,
    XCircle,
    Loader2,
    FileText,
    Copy,
    Trash2,
    ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const CampaignsDashboard = () => {
    const { token } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);
    const [campaigns, setCampaigns] = useState([]);

    const fetchData = useCallback(async () => {
        try {
            const [dashboardRes, campaignsRes] = await Promise.all([
                axios.get(`${API_URL}/admin/campaigns/dashboard`, {
                    headers: { Authorization: `Bearer ${token}` }
                }),
                axios.get(`${API_URL}/admin/campaigns?limit=10`, {
                    headers: { Authorization: `Bearer ${token}` }
                })
            ]);
            setStats(dashboardRes.data);
            setCampaigns(campaignsRes.data.campaigns || []);
        } catch (error) {
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const getStatusBadge = (status) => {
        const configs = {
            'draft': { bg: 'bg-slate-100', text: 'text-slate-700', label: 'Brouillon' },
            'scheduled': { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Programmée' },
            'sending': { bg: 'bg-blue-100', text: 'text-blue-700', label: 'En cours' },
            'sent': { bg: 'bg-green-100', text: 'text-green-700', label: 'Envoyée' },
            'partial': { bg: 'bg-orange-100', text: 'text-orange-700', label: 'Partiel' }
        };
        const config = configs[status] || configs.draft;
        return <Badge className={`${config.bg} ${config.text}`}>{config.label}</Badge>;
    };

    const getCampaignTypeLabel = (type) => {
        const labels = {
            'new_arrivals': 'Nouveaux arrivages',
            'opportunities': 'Opportunités',
            'promotions': 'Promotions',
            'stock_wanted': 'Stock recherché',
            'free': 'Campagne libre'
        };
        return labels[type] || type;
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('fr-FR', {
            day: 'numeric',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleDuplicate = async (campaignId) => {
        try {
            await axios.post(
                `${API_URL}/admin/campaigns/${campaignId}/duplicate`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Campagne dupliquée');
            fetchData();
        } catch (error) {
            toast.error('Erreur lors de la duplication');
        }
    };

    const handleDelete = async (campaignId) => {
        if (!window.confirm('Supprimer cette campagne ?')) return;
        try {
            await axios.delete(`${API_URL}/admin/campaigns/${campaignId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Campagne supprimée');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        }
    };

    if (loading) {
        return (
            <AdminLayout>
                <div className="flex items-center justify-center h-64">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                </div>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-slate-900">Communication</h1>
                        <p className="text-slate-500">Campagnes emailing B2B</p>
                    </div>
                    <Button 
                        onClick={() => navigate('/admin/communication/new')}
                        className="bg-blue-600 hover:bg-blue-700"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Nouvelle campagne
                    </Button>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    <Card>
                        <CardContent className="pt-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-blue-100 rounded-lg">
                                    <Mail className="w-5 h-5 text-blue-600" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">{stats?.total_campaigns || 0}</p>
                                    <p className="text-xs text-slate-500">Campagnes</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardContent className="pt-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-green-100 rounded-lg">
                                    <Send className="w-5 h-5 text-green-600" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">{stats?.total_emails_sent?.toLocaleString() || 0}</p>
                                    <p className="text-xs text-slate-500">Emails envoyés</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardContent className="pt-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-purple-100 rounded-lg">
                                    <Eye className="w-5 h-5 text-purple-600" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">{stats?.overall_open_rate || 0}%</p>
                                    <p className="text-xs text-slate-500">Taux ouverture</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardContent className="pt-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-amber-100 rounded-lg">
                                    <MousePointerClick className="w-5 h-5 text-amber-600" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">{stats?.overall_click_rate || 0}%</p>
                                    <p className="text-xs text-slate-500">Taux clic</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardContent className="pt-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-indigo-100 rounded-lg">
                                    <ShoppingCart className="w-5 h-5 text-indigo-600" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">{stats?.orders_generated || 0}</p>
                                    <p className="text-xs text-slate-500">Commandes</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardContent className="pt-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-emerald-100 rounded-lg">
                                    <TrendingUp className="w-5 h-5 text-emerald-600" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">{stats?.revenue_generated?.toLocaleString() || 0}€</p>
                                    <p className="text-xs text-slate-500">CA généré</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Recent Campaigns */}
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between">
                        <CardTitle className="text-lg">Campagnes récentes</CardTitle>
                        <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => navigate('/admin/communication/history')}
                        >
                            Voir tout
                            <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                    </CardHeader>
                    <CardContent>
                        {campaigns.length === 0 ? (
                            <div className="text-center py-8 text-slate-500">
                                <Mail className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                                <p>Aucune campagne créée</p>
                                <Button 
                                    className="mt-4"
                                    onClick={() => navigate('/admin/communication/new')}
                                >
                                    Créer ma première campagne
                                </Button>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {campaigns.map((campaign) => (
                                    <div 
                                        key={campaign.id}
                                        className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
                                        onClick={() => navigate(`/admin/communication/campaign/${campaign.id}`)}
                                    >
                                        <div className="flex-1">
                                            <div className="flex items-center gap-3 mb-1">
                                                <span className="font-medium text-slate-900">{campaign.name}</span>
                                                {getStatusBadge(campaign.status)}
                                                <Badge variant="outline" className="text-xs">
                                                    {getCampaignTypeLabel(campaign.campaign_type)}
                                                </Badge>
                                            </div>
                                            <div className="flex items-center gap-4 text-sm text-slate-500">
                                                <span className="flex items-center gap-1">
                                                    <Calendar className="w-3 h-3" />
                                                    {formatDate(campaign.created_at)}
                                                </span>
                                                <span>{campaign.recipients_count || 0} destinataires</span>
                                                {campaign.stats?.sent > 0 && (
                                                    <>
                                                        <span>{campaign.stats.opened || 0} ouvertures</span>
                                                        <span>{campaign.stats.clicked || 0} clics</span>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleDuplicate(campaign.id)}
                                                title="Dupliquer"
                                            >
                                                <Copy className="w-4 h-4" />
                                            </Button>
                                            {campaign.status === 'draft' && (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="text-red-600 hover:text-red-700"
                                                    onClick={() => handleDelete(campaign.id)}
                                                    title="Supprimer"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </AdminLayout>
    );
};

export default CampaignsDashboard;
